import { type RegistrationStepType } from 'interfaces';
import {
  assertClientSessionNotExpired,
  fetchUnguarded,
  type IDataStatus,
  refreshFetch,
  statusCodeHelperResponse,
} from './axios';
import { getApiEntrypoint, logger } from 'utils';
import * as Sentry from '@sentry/react';

interface SendChargeTokenPayload {
  charge_uuid: string;
  token: string;
  amount?: number | string;
}

export const getEmployments = async () => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/handbook/employment-types';
  const response = await axios({
    method: 'get',
    url: endpoint,
  });
  logger('getEmployments response', response);
  return response;
};

export const getEmploymentIndustries = async () => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/handbook/employment-industries';
  const response = await axios({
    method: 'get',
    url: endpoint,
  });
  logger('getEmploymentIndustries response', response);
  return response;
};

export const getEducations = async () => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/handbook/educations';
  const response = await axios({
    method: 'get',
    url: endpoint,
  });
  logger('getEducations response', response);
  return response;
};

export const getAddresses = async () => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/handbook/adresses';
  const response = await axios({
    method: 'get',
    url: endpoint,
  });
  logger('getAddresses response', response);
  return response;
};

export const getHyperVergeToken = async () => {
  const axios = refreshFetch();
  const endpoint = 'fo/v1/registration/selfie/token';
  const response = await axios({
    method: 'get',
    url: endpoint,
  });

  logger('getHyperVergeToken response', response);
  return response;
};

export const getLoanBySlug = async (slug: string, t?: string | null, c?: string | null) => {
  const axios = refreshFetch();
  const endpoint = `fo/functional_link/${slug}/data`;
  const params: string[] = [];

  if (t) {
    params.push(`template_id=${t}`);
  }

  if (c) {
    params.push(`campaign_id=${c}`);
  }

  const url = params.length > 0 ? `${endpoint}?${params.join('&')}` : endpoint;
  const response = await axios({
    method: 'get',
    url,
  });
  logger('getLoanBySlug response', response);
  return response;
};

export const bankAccountChangeConfirm = async (data: { agreementId: number; requestId: number; smsCode: string }) => {
  const axios = refreshFetch();
  const endpoint = '/user/bank_account/confirm_change';
  const response = await axios({
    method: 'post',
    url: endpoint,
    data,
  });
  logger('bankAccountChangeConfirm response', response);
  return response;
};

export const getBanks = async () => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/handbook/banks';
  const response = await axios({
    method: 'get',
    url: endpoint,
  });
  logger('getBanks response', response);
  return response;
};

export const getWallets = async () => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/handbook/wallets';
  const response = await axios({
    method: 'get',
    url: endpoint,
  });
  logger('getWallets response', response);
  return response;
};
export const getBankAccountType = async () => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/handbook/bank-account-types';
  const response = await axios({
    method: 'get',
    url: endpoint,
  });
  logger('getBanks response', response);
  return response;
};

export const sendChargeToken = async (payload: SendChargeTokenPayload) => {
  await assertClientSessionNotExpired();

  const axiosUnguarded = fetchUnguarded();
  const { charge_uuid: chargeUuid, token, amount } = payload;
  const response: any = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: `/fo/v1/payment/charge/${chargeUuid}/token`,
    data: {
      token,
      amount,
    },
  });
  logger('sendChargeToken response', response);

  const statusData = await statusCodeHelperResponse(response);

  return statusData;
};

export const getFile = async (uuid: string): Promise<Blob> => {
  const axios = refreshFetch();
  const endpoint = `/fo/v1/file/${uuid}`;
  const response: any = await axios({
    baseURL: getApiEntrypoint(),
    method: 'get',
    url: endpoint,
    responseType: 'blob',
  });
  logger('getFile response', response);

  // For blob responses, return the blob directly without statusCodeHelperResponse
  // as blob responses don't have the standard JSON structure with status_code
  return response.data;
};

const appendToFormData = (formData: FormData, key: string, value: any): void => {
  if (value === null || value === undefined) {
    return;
  }

  if (typeof value === 'object' && !(value instanceof Blob) && !Array.isArray(value)) {
    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
      appendToFormData(formData, `${key}[${nestedKey}]`, nestedValue);
    });
  } else {
    formData.append(key, value as string | Blob);
  }
};

export const sendFileForReview = async (uuid: string, image: any, payload?: any) => {
  const axios = refreshFetch();
  const endpoint = `/fo/v1/document-request/step`;

  if (image && !(image instanceof Blob)) {
    throw new Error('Image must be a Blob');
  }

  let requestData: FormData | object;

  if (image) {
    // Use FormData when image is provided
    const formData = new FormData();
    formData.append('imageAsBlob', image);
    formData.append('item_uuid', uuid);
    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        appendToFormData(formData, key, value);
      });
    }
    requestData = formData;
  } else {
    // Use JSON when image is null
    requestData = {
      item_uuid: uuid,
      ...(payload || {}),
    };
  }

  logger('sendFileForReview payload', {
    uuid,
    hasImage: !!image,
    payload: payload ? JSON.stringify(payload) : null,
    dataType: image ? 'FormData' : 'JSON',
  });

  const response: any = await axios({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data: requestData,
  });
  logger('sendFileForReview response', response);

  if ((response.data as IDataStatus).status_code === 'fail') {
    /* eslint-disable */
    const { status_code, data } = response.data as IDataStatus;
    const errorObj = { status_code, message: data.message };
    return await Promise.reject(errorObj);
  }

  if (response.data as RegistrationStepType) {
    return await Promise.resolve(response.data);
  }
};

export const sendErrorLogs = (payload: Record<string, unknown>): void => {
  const stage = String(payload?.stage ?? 'error');
  const normalizedError = payload?.error as { message?: string } | undefined;
  const message = typeof normalizedError?.message === 'string' ? normalizedError.message : stage;

  Sentry.captureException(new Error(message), {
    tags: {
      stage,
    },
    extra: payload,
  });
};

export const sendUserFeedback = async (payload: { comment: string; rate: number }) => {
  const axios = refreshFetch();
  const endpoint = 'fo/v1/user_feedback';
  const response = await axios({
    method: 'post',
    url: endpoint,
    data: payload,
  });

  logger('sendUserFeedback response', response);
  return response;
};
